import React, { useEffect, useState } from "react";
import "./OrderHistory.css";
import { Table } from "react-bootstrap";
import CustomPaginate from "../../../common_components/custompaginate/CustomPaginate";
import { getHistoryOfOrder } from "../../../../utils/apis/performance/performance";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const OrderHistory = () => {
  const [activeTab, setActiveTab] = useState("buy");
  const [orderHistory, setOrderHistory] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const getOrderHistory = async (page = currentPage, perPageNum = perPage) => {
    setLoading(true);
    try {
      const res = await getHistoryOfOrder(activeTab, page, perPageNum);
      if (res?.success) {
        setOrderHistory(res.data || {});
      } else {
        toast.error(res?.message || "Failed to load order history", {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "dark",
        });
      }
    } catch (error) {
      console.error("Error fetching order history:", error);
      toast.error("An error occurred while loading orders", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (page) => {
    setCurrentPage(page);
    await getOrderHistory(page, perPage);
  };

  useEffect(() => {
    getOrderHistory();
  }, [activeTab, currentPage, perPage]);

  const isBuyTab = activeTab === "buy";

  // Safe number conversion helper
  const safeNum = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // console.log("Order History:",orderHistory)
  return (
    <section className="order-history-page">
      <ToastContainer position="bottom-right" theme="dark" />

      <div className="history-content">
        <div className="mt-4 mb-6 pl-4 border-l-4 border-amber-500 bg-slate-900/40 py-3 pr-4 rounded-r-lg text-amber-200 text-sm">
  <p>
    <strong>Caution:</strong> The displayed PnL reflects <strong>only the last closed quantity</strong>. 
    It does <strong>not</strong> represent the total position PnL when partial exits have occurred.
  </p>
</div>
        {/* Segmented Tabs - Modern Pill Style */}
        <div className="tab-segmented">
          <button
            className={`tab-btn ${isBuyTab ? "active" : ""}`}
            onClick={() => setActiveTab("buy")}
          >
            Buy Orders
          </button>
          <button
            className={`tab-btn ${!isBuyTab ? "active" : ""}`}
            onClick={() => setActiveTab("sell")}
          >
            Sell Orders
          </button>
        </div>

        {/* Main Card */}
        <div className="table-card">
          <div className="table-responsive">
            <Table className="history-table" hover responsive>
              <thead>
                <tr>
                  <th>Sr. No</th>
                  <th>Company</th>
                  <th>{isBuyTab ? "Buy" : "Sell"} Price</th>
                  <th>Quantity</th>
                  <th>P/L ($)</th>
                  <th>Exit Price</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="no-data-row">
                      <div className="no-data-container">
                        <p className="no-data-text">Loading orders...</p>
                      </div>
                    </td>
                  </tr>
                ) : orderHistory?.data?.length > 0 ? (
                  orderHistory.data.map((val, index) => {
                    let Quantity = 0
                    if(val?.order_status === "close"){
                       Quantity = val?.remaining_quantity
                    }
                    else{
                       Quantity = val?.quantity
                    }
                    const entryPrice = safeNum(val?.amount) / safeNum(val?.remaining_quantity);
                    const exitPriceRaw = safeNum(val?.close_amount);
                    const plRaw = exitPriceRaw > 0 ? exitPriceRaw - entryPrice : 0;
                    const pl = plRaw.toFixed(2) * val.quantity;
                    const isProfit = plRaw >= 0;

                    return (
                      <tr key={index} className="table-row">
                        <td className="sr-no">
                          {(orderHistory.current_page - 1) * orderHistory.per_page + index + 1}
                        </td>
                        <td className="company-cell">{val?.Company?.name || "—"}</td>
                        <td className="price-cell">${entryPrice.toFixed(2)}</td>
                        <td>{val?.quantity || 0}</td>
                        <td className={`pl-cell ${isProfit ? "positive" : "negative"}`}>
                          {exitPriceRaw > 0 ? (isProfit ? "+" : "-") + `$${Math.abs(pl.toFixed(2))}` : "—"}
                        </td>
                        <td className="exit-cell">
                          {exitPriceRaw > 0 ? `$${exitPriceRaw.toFixed(2)}` : "Still Active"}
                        </td>
                        <td>{val?.type || "—"}</td>
                        <td>
                          <span className={`status-badge ${val?.order_status?.toLowerCase() || "pending"}`}>
                            {val?.order_status || "Pending"}
                          </span>
                        </td>
                        <td>
                          {val?.createdAt
                            ? new Date(val.createdAt).toLocaleDateString("en-GB")
                            : "—"}
                        </td>
                        <td>
                          {val?.createdAt
                            ? new Date(val.createdAt).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                            : "—"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="no-data-row">
                      <div className="no-data-container">
                        <p className="no-data-text">No Orders Found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {orderHistory?.total_pages > 1 && (
            <div className="pagination-wrapper">
              <CustomPaginate
                currentPage={orderHistory?.current_page}
                totalPages={orderHistory?.total_pages}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default OrderHistory;